import { FC, useMemo, useState } from 'react';

import { cleanupDataSiteROIModel, DLMROI, DLM_USER_NON_VERDICT_STATES, getLastPathFromWorkflow } from '../../../core/doclabel';
import { DLMAdminReviewForm, DLMApplicantSignedForm, DLMPhaseNoteForm, DLMProgramManagerRejectROIForm, DLMSelectReviewerForm, DLMSetupInspectionForm, DLMSubmitInspectionForm, DLMSubmitROIForm, DLMTestResultForm } from '../workflow/submit-response-modal/forms';
import { orderBy } from 'lodash';
import { useQueryClient } from 'react-query';
import { getUserResponseVars } from '../workflow/submit-response-modal/core/_helpers';
import { Form, Formik, FormikValues } from 'formik';
import { doFormSubmit, processNoteValues } from '../../../../components/helpers';
import { KTSVG } from '../../../../../_metronic/helpers';
import { ParseBillingFields, StatusErrorMessage } from '../../../../components/widgets';
import { SubmitButtonRow } from '../../../../components/layout';

type Props = {
  siteID: number
  siteState: string
  appID: number
  siteROIS: Array<DLMROI>
  siteProjectName: string
  siteClientRefNo: string
  workflow: string
  doClose: Function    
}


const skipFields = [
  'verdict',
  'workflow',
  'inspectorAgree',
];

const SiteApplicationResponseForm: FC<Props> = ({siteID, siteState, siteROIS, siteProjectName, siteClientRefNo, appID, workflow, doClose}) => {

  const queryClient = useQueryClient();
  
  const [ verdict, setVerdict ] = useState<string | null>(null)

  const { initValues, schema, submitFunc, hasBillingDetails } = useMemo(() => {
      
    let siteROIModels = [
      {
        siteId: siteID,
        projectName : siteProjectName,
        clientRefNo: siteClientRefNo,
        roiModels: orderBy(siteROIS, [' cbRef','series','no','split'], ['asc','asc','asc','asc']),
      }
    ];
    
    return getUserResponseVars(siteState, workflow, undefined, siteROIModels);
  }, [siteID, siteState, workflow, siteROIS, siteProjectName, siteClientRefNo]);
  

  const { approveText, rejectText } = useMemo(() => {

  if (siteState === 'Program Manager Rejected (A)') {
    return { approveText: 'Reply to PM', rejectText: 'Check with Applicant' };
  }

  return { approveText: 'APPROVE', rejectText : 'REJECT' };
  }, [siteState]);

  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;

    if (! submitFunc) {
      setStatus('Undefined function call');
      setSubmitting(false);
      return;
    }

    let payload = {}
    
    if (! (DLM_USER_NON_VERDICT_STATES.indexOf(siteState) === -1 && (verdict === 'rejected'))) {
      // set non-empty fields
      Object.keys(values).forEach(key => {
        // @ts-ignore
        if ((skipFields.indexOf(key) === -1) && (! key.match(/AttachmentName$/i)) && values[key]) {
          Object.assign(payload, { [key] : values[key]});
        }
      })
    }

    // set standard fields
    Object.assign(payload, {
      applicationid: appID,
      siteApplicationid: siteID,
    })

    if (DLM_USER_NON_VERDICT_STATES.indexOf(siteState) === -1) {
      Object.assign(payload, {
        approved : (values?.verdict === 'approved') ? true : false
      })
    }

    // additional cleanup
    if (
      ((siteState === 'Site Inspection Approved') && (workflow === 'Label1b1')) ||
      ((siteState === 'Program Manager Approved') && (workflow === 'Label5')) ||
      ((siteState === 'ROI Pending SCDF Verification'))
    ) {
      cleanupDataSiteROIModel(payload);
    } else if ((workflow === 'Label1b1' || workflow === 'LabelReplacement')) {
      //@ts-ignore
      if (payload.hasOwnProperty('siteROIModels')) delete payload.siteROIModels;
    }

    if (hasBillingDetails) {
      payload = ParseBillingFields(payload, values);
    }

    processNoteValues(values, payload);
    
    await doFormSubmit(submitFunc, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: getLastPathFromWorkflow(workflow) }]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      doClose();
    });
  }


  if (! initValues) {
    return null;
  }

  return(
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {({status, setFieldValue, setStatus}) => (
  <Form id='kt_doclabel_siteapplication_response_form' className='form' noValidate>
    <div
      className='d-flex flex-column p-4'            
    >
      { // WITH VERDICT
      (DLM_USER_NON_VERDICT_STATES.indexOf(siteState) === -1) &&
      <>
      <div className="text-muted fw-normal fs-6 mb-2">Select verdict:</div>
      <div className='fv-row mb-2 d-flex flex-row gap-4'>
        <div
          className='flex-root' onClick={() => {
            setVerdict('approved');
            setFieldValue('verdict', 'approved');
            setStatus('');
          }}
        >
          <input
            id='kt_submit_review_type_approved'
            type='radio'
            className='btn-check'
            name="verdict"
            value='approved'
          />
          <label
            className={'btn py-2 px-6 d-flex flex-row align-items-center mb-2 ' + (verdict === 'approved' ? 'btn-success text-white' : ' btn-outline btn-active-color-primary btn-outline-dashed btn-outline-default')}
            htmlFor='kt_submit_review_type_approved'
          >
            <KTSVG
              path='/media/icons/duotune/abstract/abs009.svg'
              className='svg-icon-2x me-5'
            />

            <div className='flex-grow-1 fw-bold text-center pe-10'>
              <span className='fw-bolder d-block fs-4'>{approveText}</span>
            </div>
          </label>
        </div>
        <div className='flex-root'
          onClick={() => {
            setVerdict('rejected');
            setFieldValue('verdict', 'rejected');
            setStatus('');
          }}
        >
          <input
            id='kt_submit_review_type_rejected'
            type='radio'
            className='btn-check'
            name="verdict"
            value='rejected'
          />
          <label
            className={'btn py-2 px-6 d-flex flex-row align-items-center mb-2 ' + (verdict === 'rejected' ? 'btn-danger' : ' btn-outline btn-active-color-danger btn-outline-dashed btn-outline-default')}
            htmlFor='kt_submit_review_type_rejected'
          >
            <KTSVG
              path='/media/icons/duotune/abstract/abs012.svg'
              className='svg-icon-2x me-5'
            />
            <div className='flex-grow-1 fw-bold text-center pe-10'>
              <span className='fw-bolder d-block fs-4'>{rejectText}</span>
            </div>
          </label>
        </div>
      </div>
      { verdict &&
      <>
      <div className='separator border-gray-200 mb-4'></div>
      { (siteState === 'Admin Review') &&
      <DLMAdminReviewForm /> 
      }
      
      { (
        (workflow === 'Label5') &&
        (siteState === 'Site Inspection')
      ) &&
      <DLMSelectReviewerForm/> }

      {(
        (workflow === 'Label1b1') &&
        (siteState === 'Site Inspection')
      ) &&
      <DLMSubmitInspectionForm/> }

      { ( (workflow === 'Label5') && (siteState === 'Program Manager Rejected (A)') ) &&
      <DLMProgramManagerRejectROIForm /> }


      { (siteState ==='Applicant Signed') && 
      <DLMApplicantSignedForm />
      }

      { (
        ((workflow === 'Label5') && (siteState === 'Program Manager Approval'))                
        || (siteState ==='Site Inspection Review')
        || (siteState ==='Pending Component Check')
      ) &&
      <DLMPhaseNoteForm/> 
      }            
      </>
      }
      </>
      }

      { // NON VERDICT STATES
      (DLM_USER_NON_VERDICT_STATES.indexOf(siteState) > -1) &&
      <>
      { ((['Testing in Progress','Inspector Reviewer']).indexOf(siteState) > -1) &&
      <DLMTestResultForm/> 
      }

      {(
        ((workflow === 'Label5' || workflow === 'Label1b1') && (siteState === 'Site Inspection Approved')) ||
        ((siteState === 'Program Manager Approved') && (workflow === 'Label5')) ||
        ((siteState === 'ROI Pending SCDF Verification'))
      )  &&
      <DLMSubmitROIForm/> 
      }


      { ((workflow === 'Label5' && siteState === 'Inspection Setup')) &&
      <DLMSetupInspectionForm/> 
      }

      { ( ((workflow === 'Label5') && siteState === 'Inspector Reviewer') ) &&
      <DLMPhaseNoteForm/> 
      }
      </>
      }

      { (DLM_USER_NON_VERDICT_STATES.indexOf(siteState) > -1 || verdict) &&
      <>
      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={doClose} /> 
      </>
      }
    </div>
  </Form>
  )}
  </Formik>
  )
}

export {SiteApplicationResponseForm}
